/* Created by Interactive 63 - https://interactive63.com */
/**
 * Finds the elements that need animation output and the timing the scene uses. An element is
 * animated when it has at least one enabled CSS track with a keyframe.
 */
import type { ElementId, ElementMeta, Project, Track } from '../model/types';
import { PROPERTIES } from '../properties/registry';
import type { AnimatedElement, CompileTarget, SceneTiming } from './types';

const PROPERTY_ORDER = Object.keys(PROPERTIES);

interface SourceFacts {
  /** IDs of elements that carry a transform attribute in the stored source. */
  withTransform: Set<ElementId>;
}

let cachedSource = '';
let cachedFacts: SourceFacts = { withTransform: new Set() };

/** Reads which elements have a transform attribute, remembering the last result by source text. */
function sourceFacts(source: string): SourceFacts {
  if (source === cachedSource) return cachedFacts;
  const doc = new DOMParser().parseFromString(source, 'image/svg+xml');
  const withTransform = new Set<ElementId>();
  for (const el of Array.from(doc.querySelectorAll('[data-st-id][transform]'))) {
    const id = el.getAttribute('data-st-id');
    if (id) withTransform.add(id);
  }
  cachedSource = source;
  cachedFacts = { withTransform };
  return cachedFacts;
}

/** Whether an element, or any ancestor, is excluded from export. */
export function isExcluded(project: Project, id: ElementId): boolean {
  const seen = new Set<ElementId>();
  for (let current: ElementId | null = id; current && !seen.has(current);) {
    seen.add(current);
    const element: ElementMeta | undefined = project.elements[current];
    if (!element) return false;
    if (element.excludeFromExport) return true;
    current = element.parentId;
  }
  return false;
}

/** Whether a track contributes CSS output. */
function isCssTrack(track: Track): boolean {
  // Morphs and words compile through their own modules.
  return (
    track.enabled &&
    track.keyframes.length > 0 &&
    track.property !== 'morph' &&
    track.property !== 'words'
  );
}

/** Collects animated elements in document (ID) order, each with tracks in registry order. */
export function collectAnimated(project: Project, target: CompileTarget): AnimatedElement[] {
  const byElement = new Map<ElementId, Track[]>();
  for (const track of project.tracks) {
    if (!isCssTrack(track)) continue;
    if (target === 'export' && isExcluded(project, track.elementId)) continue;
    const list = byElement.get(track.elementId) ?? [];
    list.push(track);
    byElement.set(track.elementId, list);
  }

  const facts = byElement.size > 0 ? sourceFacts(project.svg.source) : null;
  const animated: AnimatedElement[] = [];
  for (const [id, tracks] of byElement) {
    const meta = project.elements[id];
    if (!meta) continue;
    tracks.sort((a, b) => PROPERTY_ORDER.indexOf(a.property) - PROPERTY_ORDER.indexOf(b.property));
    const wrapped =
      (facts?.withTransform.has(id) ?? false) &&
      tracks.some((t) => PROPERTIES[t.property].usesTransform);
    animated.push({ id, meta, tracks, wrapped });
  }
  return animated.sort((a, b) => elementOrder(a.id) - elementOrder(b.id));
}

/** Numeric order of an element ID such as "e12". */
function elementOrder(id: ElementId): number {
  return Number(id.slice(1));
}

/** Resolves the timing for a target: previews play one undelayed iteration. */
export function resolveTiming(project: Project, target: CompileTarget): SceneTiming {
  const { scene } = project;
  if (target === 'preview') {
    return {
      duration: scene.duration,
      delay: 0,
      iterations: 1,
      direction: 'normal',
      fillMode: 'both',
    };
  }
  return {
    duration: scene.duration,
    delay: scene.delay,
    iterations: scene.iterations,
    direction: scene.direction,
    fillMode: scene.fillMode,
  };
}
